import {
  ArrowLeftRightIcon,
  BufferIcon,
  ChannelIcon,
  Dialog,
  Flex,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import type { ChannelType } from '@buffer-mono/popcorn'
import { constructTitleWithGrammer } from './constructTitleWithGrammer'
import ChannelsList from './components/ChannelsList'
import { ConnectChannelUpgradeActions } from './components/ConnectChannelUpgradeActions'
import { ConnectChannelUpgradeMessage } from './components/ConnectChannelUpgradeMessage'
import useChannels from './../../hooks/useChannels'

import type { Service } from '../../../../exports/Orchestrator/channelConnections/types'

import styles from './ConnectChannelUpgrade.module.css'
import { usePlanSelectorQuery } from '../../../PlanSelector/hooks/usePlanSelectorQuery'

import type { JSX } from 'react'

const ConnectChannelUpgrade = ({
  onOpenChange,
}: {
  onOpenChange: () => void
}): JSX.Element => {
  const { channelIds, unlockedChannels, lockedChannels, lockedChannelIds } =
    useChannels()
  const totalConnectedChannels = unlockedChannels.concat(lockedChannels)
  const title = constructTitleWithGrammer(channelIds.length)

  // Non-agency tiered plans are the only ones with more than 2 pricing tiers.
  const { data: planSelectorData } = usePlanSelectorQuery()
  const isOnTieredPricing =
    (planSelectorData?.subscription.plan.pricing.length ?? 0) > 2

  const lastConnectedChannelService = totalConnectedChannels[
    totalConnectedChannels?.length - 1
  ]?.service as Service

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <Dialog.Content className={styles.content}>
        <Flex
          direction="column"
          gap="xl"
          align="center"
          fullWidth
          fullHeight
          className={styles.contentWrapper}
          data-testid={`channel-connection-update-modal-body`}
        >
          <Flex justify="center" gap="sm" fullWidth>
            <BufferIcon />
            {lastConnectedChannelService && (
              <>
                <ArrowLeftRightIcon color="subtle" />
                <ChannelIcon
                  type={lastConnectedChannelService as ChannelType}
                  color="branded"
                />
              </>
            )}
          </Flex>
          <Dialog.CloseButton />
          <Dialog.Body>
            <Flex
              direction="column"
              align="center"
              justify="center"
              gap="lg"
              fullWidth
              fullHeight
            >
              <Dialog.Title size="lg">{title}</Dialog.Title>
              <VisuallyHidden>
                <Dialog.Description>
                  Channel connection upgrade details
                </Dialog.Description>
              </VisuallyHidden>
              <ConnectChannelUpgradeMessage
                totalChannelsCount={totalConnectedChannels.length}
                lockedChannelIds={lockedChannelIds}
                isOnTieredPricing={isOnTieredPricing}
              />
              <ChannelsList channels={totalConnectedChannels} />
            </Flex>
          </Dialog.Body>
        </Flex>
        <Dialog.Footer className={styles.footer}>
          <ConnectChannelUpgradeActions />
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default ConnectChannelUpgrade
